import React from 'react';

export interface RejectionResource {
  name: string;
  tag?: string;
}

interface AxisRejectionPopupProps {
  intro: string;
  resources: RejectionResource[];
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  resourcesHeading?: string;
}

/**
 * Popup shown when a patient's pre-qualification answers indicate they are not
 * a good fit for online booking. Renders an intro message plus a formatted list
 * of recommended community resources.
 */
const AxisRejectionPopup: React.FC<AxisRejectionPopupProps> = ({
  intro,
  resources,
  isOpen,
  onClose,
  title = 'We may not be the right fit',
  resourcesHeading = 'Recommended resources',
}) => {
  // Handle escape key + lock body scroll while open
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-50 modal-backdrop"
      onClick={handleBackdropClick}
      style={{ pointerEvents: 'auto', padding: '16px' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rejection-popup-title"
      aria-describedby="rejection-popup-desc"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full modal-content relative"
        onClick={(e) => e.stopPropagation()}
        style={{
          pointerEvents: 'auto',
          maxWidth: '480px',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'Montserrat, Arial, sans-serif',
        }}
      >
        {/* Header */}
        <div style={{ padding: '28px 28px 16px', position: 'relative' }}>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              lineHeight: 1,
              color: '#9ca3af',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            ×
          </button>
          <h2
            id="rejection-popup-title"
            style={{ color: '#01545d', fontSize: '1.4rem', fontWeight: 700, margin: '0 32px 0 0' }}
          >
            {title}
          </h2>
        </div>

        {/* Scrollable body */}
        <div style={{ padding: '0 28px', overflowY: 'auto' }}>
          <p
            id="rejection-popup-desc"
            style={{ color: '#374151', fontSize: '0.95rem', lineHeight: 1.6, margin: '0 0 20px' }}
          >
            {intro}
          </p>

          <div
            style={{
              color: '#01545d',
              fontSize: '0.8rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              marginBottom: '10px',
            }}
          >
            {resourcesHeading}
          </div>

          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {resources.map((resource) => (
              <li
                key={resource.name}
                style={{
                  background: '#f7f8fd',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                }}
              >
                {resource.tag && (
                  <span style={{ color: '#6b7280', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    {resource.tag}
                  </span>
                )}
                <span style={{ color: '#111827', fontSize: '0.95rem', fontWeight: 600 }}>{resource.name}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div style={{ padding: '20px 28px 24px' }}>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              backgroundColor: '#01545d',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              padding: '14px',
              fontSize: '1rem',
              fontWeight: 700,
              cursor: 'pointer',
              pointerEvents: 'auto',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AxisRejectionPopup;
